import React, { useState, useEffect } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { bool, func, object } from 'prop-types'
import Confirm from '../Confirm/Confirm'
import ConfirmSelect from '../ConfirmSelect/ConfirmSelect'
import API from '../../utils/apiClient'

const CampusesDeleteWizard = ({
  closeCallback,
  deleteRecord,
  isOpen,
  stateCallback
}) => {
  // state
  const [currentStep, setCurrentStep] = useState(1)
  const [campuses, setCampuses] = useState([])
  const [selectedCampus, setSelectedCampus] = useState()

  const notify = useNotify()
  const refresh = useRefresh()

  // utility functions
  const renderConfirmSelect = (handleSelection, options, selectedOption) => {
    return (
      <ConfirmSelect
        handleSelection={handleSelection}
        inputText="Select a campus"
        options={options}
        selectedOption={selectedOption}
        text='Which campus would you like to merge the data into?'
      />
    )
  }

  const setNextStep = action => {
    if (stepData[currentStep][action] === undefined) {
      handleClose()
    } else {
      setCurrentStep(stepData[currentStep][action])
    }
  }

  // constants
  const stepData = {
    1: {
      cancelStep: undefined,
      cancelText: 'No',
      confirmStep: 2,
      confirmText: 'Yes',
      content: 'Are you sure you want to delete this campus?'
    },
    2: {
      cancelStep: 5,
      cancelText: 'No',
      confirmStep: 3,
      confirmText: 'Yes',
      content: 'Would you like to merge all of the campus data to another campus?'
    },
    3: {
      cancelStep: 2,
      cancelText: 'Back',
      confirmDisable: !selectedCampus,
      confirmStep: 4,
      confirmText: 'Continue',
      content: () => renderConfirmSelect(handleCampusSelect, campuses, selectedCampus)
    },
    4: {
      cancelStep: 3,
      cancelText: 'No',
      confirmStep: undefined,
      confirmText: 'Yes',
      content: `Are you sure you would like to delete ${deleteRecord && deleteRecord.name} and merge its data into ${selectedCampus && selectedCampus.name}?`
    },
    5: {
      cancelStep: 2,
      cancelText: 'Back',
      confirmStep: 6,
      confirmText: 'Continue',
      content: 'All data for this campus will be permanently lost.'
    },
    6: {
      cancelStep: 2,
      cancelText: 'Back',
      confirmStep: undefined,
      confirmText: 'Continue',
      content: 'There is no turning back now.'
    }
  }

  // effects
  useEffect(() => {
    const getCampuses = async () => {
      let response

      if (isOpen && campuses.length === 0) {
        try {
          response = await API.GET(
            '/api/v1/campuses',
            {},
            {
              fields: 'id,name',
              limit: 5000
            }
          )
        } catch (err) {
          console.error(err)
        }
      }

      if (response) setCampuses(response.data.content)
    }

    getCampuses()
  }, [isOpen])

  // handlers
  const handleCancel = () => {
    setNextStep('cancelStep')
  }

  const handleClose = () => {
    setCurrentStep(1)
    setSelectedCampus()
    closeCallback()
    stateCallback()
  }

  const handleConfirm = async () => {
    setNextStep('confirmStep')
    let response
    if (currentStep === 4) {
      try {
        response = await API.POST(
          `/api/v1/campuses/${deleteRecord.id}/merge`,
          {},
          {
            target_campus_id: selectedCampus.id,
          }
        )
        refresh()
        notify(`${deleteRecord.name} Deleted`, 'info')
        return  {
          data: response
        }
      } catch (err) {
        console.error(err)
        notify(`${deleteRecord.name} Could Not Be Deleted`, 'warning')
      }
    }
    if (currentStep === 6) {
      try {
        response = await API.DELETE(`/api/v1/campuses/${deleteRecord.id}`)
        refresh()
        notify(`${deleteRecord.name} Deleted`, 'info')
        return  {
          data: response
        }
      } catch (err) {
        console.error(err)
        notify(`${deleteRecord.name} Could Not Be Deleted`, 'warning')
      }
    }

  }

  const handleCampusSelect = (e, value) => {
    setSelectedCampus(value)
  }

  return (
    <Confirm
      cancelText={stepData[currentStep].cancelText}
      confirmDisable={stepData[currentStep].confirmDisable}
      confirmText={stepData[currentStep].confirmText}
      handleCancel={handleCancel}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      isOpen={isOpen}
      content={stepData[currentStep].content}
    />
  )
}

CampusesDeleteWizard.propTypes = {
  closeCallback: func,
  deleteRecord: object,
  isOpen: bool,
  stateCallback: func
}

export default CampusesDeleteWizard
